import type {
  AudioBus,
  EventBus,
  ExitReason,
  GameContext,
  GameEventMap,
  InputManager,
  InputState,
  PRNG,
  ProgressStore,
  ScoreEntry,
  ScoreStore,
  StorageAPI,
} from '@arcraz/arcadia-types';
import { createPrng } from '@arcraz/arcadia-core';
import { createAudioBus, type CabinetAudioBus } from './audio.js';

/**
 * Build a `GameContext` for the cabinet at runtime.
 *
 * Real implementations:
 *   - `random` — seeded PRNG from `@arcraz/arcadia-core` (sc-2773)
 *   - `events` — in-memory pub/sub
 *
 * Stub implementations (real impls land in epic:runtime stories):
 *   - `audio` — no-op AudioBus
 *   - `input.snapshot()` — returns the latest snapshot the cabinet handed
 *     to the cartridge (we cache it; the cabinet's runtime loop calls
 *     `instance.input(state)` directly, so cartridges typically don't
 *     reach for `ctx.input.snapshot()`)
 *   - `storage` — in-memory ScoreStore + ProgressStore
 *
 * `exit` is the caller-supplied callback the cartridge fires on game-over /
 * player-quit / etc. The cabinet shell hooks it to navigate back to the
 * selector (eventually) or just to flip into the GAME OVER restart-prompt
 * state (today).
 */
export interface BuildContextOpts {
  seed: string;
  onExit: (reason: ExitReason) => void;
  /**
   * Cabinet-owned AudioBus. The cabinet creates one bus at startup and
   * shares it across cartridge launches so mute state + AudioContext
   * survive selector navigation. If omitted (e.g. headless tests), a
   * no-op fallback is used so existing test stubs continue to work.
   */
  audio?: AudioBus;
}

export function buildGameContext(opts: BuildContextOpts): GameContext {
  const random = createPrng(opts.seed);
  const events = createEventBus();
  const audio = opts.audio ?? createNoopAudioBus();
  const storage = createInMemoryStorage();
  const input = createInputManagerStub();

  return {
    audio,
    input,
    storage,
    random,
    events,
    exit: opts.onExit,
  };
}

/**
 * Cabinet-level audio bus. Created lazily on first access so it doesn't
 * spin up an AudioContext during SSR / tests.
 */
let sharedAudioBus: CabinetAudioBus | null = null;
export function getCabinetAudioBus(): CabinetAudioBus {
  if (!sharedAudioBus) {
    sharedAudioBus = createAudioBus();
    if (typeof window !== 'undefined') {
      // Debug handle. Lets the player poke at the bus from devtools:
      //   window.__arcadia84_audio.sfx('shoot')
      (window as unknown as { __arcadia84_audio?: CabinetAudioBus }).__arcadia84_audio =
        sharedAudioBus;
    }
  }
  return sharedAudioBus;
}

// ============================================================================
// Implementations
// ============================================================================

function createEventBus(): EventBus {
  const handlers = new Map<keyof GameEventMap, Set<(payload: unknown) => void>>();
  return {
    emit<K extends keyof GameEventMap>(event: K, payload: GameEventMap[K]): void {
      const set = handlers.get(event);
      if (!set) {
        return;
      }
      for (const handler of set) {
        handler(payload);
      }
    },
    on<K extends keyof GameEventMap>(
      event: K,
      handler: (payload: GameEventMap[K]) => void
    ): () => void {
      let set = handlers.get(event);
      if (!set) {
        set = new Set();
        handlers.set(event, set);
      }
      const cast = handler as (payload: unknown) => void;
      set.add(cast);
      return () => {
        set?.delete(cast);
      };
    },
  };
}

function createNoopAudioBus(): AudioBus {
  return {
    sfx(): void {
      /* MVP: silent */
    },
    synth(): void {
      /* MVP: silent */
    },
    loop(): { setFrequency: (hz: number) => void; setGain: (g: number) => void; stop: () => void } {
      return { setFrequency: () => {}, setGain: () => {}, stop: () => {} };
    },
    bgm(): void {
      /* MVP: silent */
    },
    stopBgm(): void {
      /* MVP: silent */
    },
    duck(): void {
      /* MVP: silent */
    },
  };
}

function createInMemoryStorage(): StorageAPI {
  const scores: ScoreEntry[] = [];
  let progress: unknown = null;

  const scoreStore: ScoreStore = {
    async add(entry: ScoreEntry): Promise<number> {
      scores.push(entry);
      scores.sort((a, b) => b.score - a.score);
      const rank = scores.findIndex((e) => e === entry) + 1;
      return rank;
    },
    async top(limit: number): Promise<ScoreEntry[]> {
      return scores.slice(0, limit);
    },
    async bestFor(displayName: string): Promise<ScoreEntry | null> {
      const matches = scores.filter((e) => e.displayName === displayName);
      return matches[0] ?? null;
    },
  };

  const progressStore: ProgressStore = {
    async get<T = unknown>(): Promise<T | null> {
      return progress as T | null;
    },
    async update<T = unknown>(partial: Partial<T>): Promise<void> {
      progress = { ...(progress as object), ...(partial as object) };
    },
    async reset(): Promise<void> {
      progress = null;
    },
  };

  return {
    scores: scoreStore,
    progress: progressStore,
  };
}

const EMPTY_INPUT_STATE: InputState = Object.freeze({
  up: false,
  down: false,
  left: false,
  right: false,
  fireA: false,
  fireB: false,
  fireC: false,
  start: false,
  coin: false,
  stickL: Object.freeze({ x: 0, y: 0 }),
  stickR: Object.freeze({ x: 0, y: 0 }),
  pointer: Object.freeze({ x: -1, y: -1, down: false }),
  justPressed: Object.freeze(new Set()),
  justReleased: Object.freeze(new Set()),
}) as InputState;

function createInputManagerStub(): InputManager {
  return {
    snapshot(): InputState {
      // Cartridges receive InputState directly via instance.input(state) each
      // tick. This snapshot() exists for non-frame queries; the empty state
      // is a safe default for the MVP.
      return EMPTY_INPUT_STATE;
    },
    hasGamepad(): boolean {
      return false;
    },
    isTouch(): boolean {
      // Feature detection only — no UA sniffing. `ontouchstart` covers
      // most touch devices; `(pointer: coarse)` catches devices with no
      // touchstart but a coarse primary pointer (some PWAs, some
      // Surface-style tablets in tablet mode). A hybrid device (laptop
      // with touchscreen) returns true — that's fine, both input modes
      // composite into the same InputState via the loop machinery, and
      // touch affordances rendering alongside keyboard hints is helpful
      // rather than wrong.
      if (typeof window === 'undefined') return false;
      if ('ontouchstart' in window) return true;
      if (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(pointer: coarse)').matches
      ) {
        return true;
      }
      return false;
    },
  };
}

// Pull PRNG-related re-exports through so consumers can import everything
// they need from this module without reaching into core directly.
export type { PRNG };

/**
 * Top-level touch-device detection, mirroring the GameContext `isTouch()`
 * method so non-cartridge code (e.g., the touch input overlay) can probe
 * the same way without manufacturing a context.
 */
export function isTouch(): boolean {
  if (typeof window === 'undefined') return false;
  if ('ontouchstart' in window) return true;
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  ) {
    return true;
  }
  return false;
}
